import React, { useCallback, useEffect, useState } from 'react'
import clsx from 'clsx'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import Layout from '../components/Layout'
import PaymentService from '../services/PaymentService'
import { useSettings } from '../hooks/useSettings'

// Esito dell'evasione lato server (services/checkout_sessions.py) -> cosa dire.
const VIEW_BY_RESULT = {
  granted: 'success',
  already_granted: 'success',
  pending: 'processing',
  not_paid: 'failed',
}

/**
 * Ritorno dalla Checkout incorporata (Managed Payments): Stripe riporta il
 * browser su /payment-completion?session_id=… a pagamento concluso.
 *
 * Il server rilegge la sessione da Stripe e la evade (stesso accredito,
 * idempotente, del webhook e della riconciliazione oraria): questa pagina non
 * decide niente, mostra soltanto l'esito. Un pagamento con metodo differito
 * resta «in elaborazione» e i crediti arrivano col webhook.
 */
const CheckoutSessionReturn = ({ sessionId }) => {
  const { t } = useTranslation('payments')
  const navigate = useNavigate()
  const { isDarkMode } = useSettings()
  const [outcome, setOutcome] = useState(null)
  const backToMainPage = useCallback(() => navigate('/'), [navigate])

  useEffect(() => {
    let alive = true
    PaymentService.confirmCheckout(sessionId)
      .then((r) => {
        if (!alive) return
        const data = r?.data || {}
        setOutcome({
          view: VIEW_BY_RESULT[data.result] || 'error',
          credits: data.credits,
        })
      })
      .catch(() => alive && setOutcome({ view: 'error' }))
    return () => {
      alive = false
    }
  }, [sessionId])

  const messages = {
    success: t('checkout.return.creditsAdded', {
      credits: outcome?.credits ?? '',
    }),
    processing: t('checkout.return.processing'),
    failed: t('checkout.return.notPaid'),
    error: t('checkout.return.error'),
  }

  return (
    <div
      className={clsx('relative w-screen h-screen overflow-hidden', {
        'bg-dark-blue text-white': isDarkMode,
        'bg-light-blue text-mid-blue': !isDarkMode,
      })}
    >
      <Layout>
        <main className="h-[calc(100svh-30px)] desktop:h-[calc(100svh-54px)] flex justify-center items-center">
          {!outcome ? (
            <img className="mx-auto" src="/assets/images/loader.svg" alt="" />
          ) : (
            <div
              data-testid={`checkout-return-${outcome.view}`}
              className={clsx(
                'w-[350px] rounded-lg p-6 flex flex-col gap-6 items-center shadow-[4px_4px_10px_0px_#00000040]',
                { 'bg-mid-blue': isDarkMode, 'bg-white': !isDarkMode }
              )}
            >
              <h1 className="text-xl tracking-wide text-center">
                {t(`checkout.return.heading.${outcome.view}`)}
              </h1>
              <p className="text-center font-light">{messages[outcome.view]}</p>
              <button
                type="button"
                data-testid="checkout-return-back"
                className={clsx(
                  'h-[40px] w-full text-white text-sm rounded-xl transition duration-100',
                  { 'bg-blue': isDarkMode, 'bg-sky-blue': !isDarkMode }
                )}
                onClick={backToMainPage}
              >
                {t('checkout.return.back')}
              </button>
            </div>
          )}
        </main>
      </Layout>
    </div>
  )
}

export default CheckoutSessionReturn
